import {
  clearGetAllUsers,
  deleteUser,
  getAllUsers,
} from "../../../Redux/Users/UsersActions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { ICellRendererParams, ColDef, GridReadyEvent } from "ag-grid-community";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import exportToExcel from "../../../Util/exportToExcel";
import { AppDispatch } from "../../../Redux/store";
import React, { useEffect, useState } from "react";
import { RootState } from "../../../Redux/store";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import styles from "./Users.module.scss";
import { Link } from "react-router";

interface UsersType {
  firstName: string;
  lastName: string;
  emailAddress: string;
  role: {
    roleName: string;
  };
  isActive: boolean;
  userId: number;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Users() {
  const dispatch = useDispatch<AppDispatch>();
  const { users, usersLoading, deleteUserLoading } = useSelector(
    (state: RootState) => state.users,
  );
  const [rowData, setRowData] = useState<UsersType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<number>(-1);
  const navigate = useNavigate();

  const filteredUsers = rowData?.filter(
    (user: UsersType) =>
      user?.firstName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      user?.lastName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      user?.emailAddress?.toLowerCase()?.includes(search?.toLowerCase()) ||
      user?.role?.roleName?.toLowerCase()?.includes(search?.toLowerCase()),
  );

  const deleteUserConfirmationHandler = () => {
    const successHandler = () => {
      setIsDeleteModalOpen(false);
      setDeleteUserId(-1);
    };
    dispatch(
      deleteUser({
        id: deleteUserId,
        successHandler,
      }),
    );
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const columnDefs: ColDef<UsersType>[] = [
    {
      headerName: "Name",
      flex: 1, // changed from fixed width to flex
      minWidth: 150,
      valueGetter: (params) =>
        (params?.data?.firstName || "") + " " + (params?.data?.lastName || ""),
    },
    {
      headerName: "Email",
      field: "emailAddress",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Role",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params?.data?.role?.roleName,
    },
    {
      headerName: "Status",
      field: "isActive",
      flex: 1,
      minWidth: 100,
      valueGetter: (params) =>
        params?.data?.isActive ? "Active" : "In-Active",
      cellClass: (params) => {
        return params?.data?.isActive === true
          ? "CompletedText"
          : params?.data?.isActive === false
            ? "pendingText"
            : "text-black-500";
      },
    },
    {
      sortable: false,
      suppressAutoSize: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      cellStyle: {
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className={styles.iconContainer}>
            <Menu
              direction="left"
              menuButton={
                <MenuButton>
                  <HiDotsVertical className={styles.icon} />
                </MenuButton>
              }
              transition
              portal
            >
              <MenuItem
                onClick={() =>
                  navigate(`/admin/users/edit/${params?.data?.userId}`)
                }
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setIsDeleteModalOpen(true);
                  setDeleteUserId(params?.data?.userId);
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllUsers());

    return () => {
      dispatch(clearGetAllUsers());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (users) {
      setRowData(users);
    }
  }, [users]);

  if (usersLoading) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onConfirm={deleteUserConfirmationHandler}
        heading="Delete User"
        text="Are you sure you want to delete this user?"
        loading={deleteUserLoading}
      />
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Manage Users</h1>
          </div>
          <div className={styles.buttonContainer}>
            {filteredUsers?.length > 0 && (
              <button
                className={styles.button}
                onClick={() =>
                  exportToExcel({
                    data: filteredUsers || [],
                    fileName: "Users",
                  })
                }
              >
                Export
              </button>
            )}
            <Link to="/admin/users/add" className={styles.buttonContainer}>
              <button className={styles.button}>Add User</button>
            </Link>
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
            />
            <div className={styles.iconContainer} onClick={() => setSearch("")}>
              <IoCloseSharp className={styles.icon} />
            </div>
          </div>
          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredUsers}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default Users;
